import axios from 'axios';

import { API_LIMIT } from 'constants/api';
import { BribeSortFields, BribesTypes, BribesWeeksFilters } from 'constants/bribes';

import { getAssetString } from 'helpers/assets';
import { getNextWeekStartFromString } from 'helpers/date';
import { getBribesApiUrl, getMarketKeysApiUrl, getVotingTrackerApiUrl } from 'helpers/url';

import type { UpcomingBribe } from 'types/bribes';
import type { Token } from 'types/token';
import type { ListResponse, MarketBribes, MarketKey, MarketVotes } from 'types/vote';

/**
 * Fetches the market key data for a specific pair of tokens.
 * Returns `null` if the request fails.
 */
export const getMarketPair = async (base: Token, counter: Token): Promise<MarketKey | null> => {
    try {
        const { data } = await axios.get<MarketKey>(
            `${getMarketKeysApiUrl()}${getAssetString(base)}-${getAssetString(counter)}`,
        );
        return data;
    } catch {
        return null;
    }
};

const processBribes = async (
    results: UpcomingBribe[] | MarketBribes[],
    count: number,
): Promise<{
    count: number;
    bribes: (UpcomingBribe & Partial<MarketKey> & Partial<MarketVotes>)[];
}> => {
    if (!results.length) return { count, bribes: [] };

    // Prepare URL query parameters for bulk requests
    const keysParams = new URLSearchParams();
    const votesParams = new URLSearchParams();

    results.forEach(bribe => {
        keysParams.append('account_id', bribe.market_key);
        votesParams.append('market_key', bribe.market_key);
    });

    // Fetch market key and voting data in parallel
    const [marketsVotesRes, marketsKeysRes] = await Promise.all([
        axios.get<ListResponse<MarketVotes>>(getVotingTrackerApiUrl(), { params: votesParams }),
        axios.get<ListResponse<MarketKey>>(getMarketKeysApiUrl(), { params: keysParams }),
    ]);

    const marketsVotes = marketsVotesRes.data.results;
    const marketsKeys = marketsKeysRes.data.results;

    // Combine bribe data with its associated market key and votes
    const bribes = results.map(bribe => {
        const marketKey = marketsKeys.find(k => k.account_id === bribe.market_key);
        const marketVote = marketsVotes.find(v => v.market_key === bribe.market_key);
        return { ...bribe, ...marketKey, ...marketVote };
    });

    return { count, bribes };
};

const getCurrentBribeMarkets = async (): Promise<MarketBribes[]> => {
    const getBribesPage = (page: number) =>
        axios
            .get<ListResponse<MarketBribes>>(`${getBribesApiUrl()}bribes/`, {
                params: { limit: API_LIMIT, page },
            })
            .then(({ data }) => data);

    const firstPage = await getBribesPage(1);

    if (!firstPage.next) {
        return firstPage.results;
    }

    const pagesCount = Math.ceil(firstPage.count / API_LIMIT);
    const restPages = await Promise.all(
        Array.from({ length: pagesCount - 1 }, (_, index) => getBribesPage(index + 2)),
    );

    return restPages.reduce((acc, page) => [...acc, ...page.results], [...firstPage.results]);
};

const getMarketKeysByAccountIds = async (accountIds: string[]): Promise<MarketKey[]> => {
    if (!accountIds.length) {
        return [];
    }

    const requests: Promise<MarketKey[]>[] = [];

    for (let index = 0; index < accountIds.length; index += API_LIMIT) {
        const chunk = accountIds.slice(index, index + API_LIMIT);
        const params = new URLSearchParams();

        chunk.forEach(accountId => params.append('account_id', accountId));
        params.append('limit', chunk.length.toString());

        requests.push(
            axios
                .get<ListResponse<MarketKey>>(getMarketKeysApiUrl(), { params })
                .then(({ data }) => data.results),
        );
    }

    return Promise.all(requests).then(results => results.flat());
};

export const getCurrentBribeMarketsForAsset = async (asset: Token): Promise<MarketBribes[]> => {
    const bribeMarkets = await getCurrentBribeMarkets();

    if (!bribeMarkets.length) {
        return [];
    }

    const assetString = getAssetString(asset);
    const marketKeys = await getMarketKeysByAccountIds([
        ...new Set(bribeMarkets.map(({ market_key }) => market_key)),
    ]);
    const marketKeysMap = marketKeys.reduce((acc, marketKey) => {
        acc.set(marketKey.account_id, marketKey);
        return acc;
    }, new Map<string, MarketKey>());

    return bribeMarkets.filter(({ market_key }) => {
        const marketKey = marketKeysMap.get(market_key);

        return marketKey && (marketKey.asset1 === assetString || marketKey.asset2 === assetString);
    });
};

type UpcomingRequestParams = {
    limit: number;
    page: number;
    ordering: BribeSortFields;
    start_at__gte?: string;
    start_at__lt?: string;
    aqua_total_reward_amount_equivalent__gte: string;
    is_amm_protocol?: boolean;
};

export const getUpcomingBribes = async (
    pageSize: number,
    page: number,
    sort: BribeSortFields,
    minBribeAmount: string,
    weekFilter: BribesWeeksFilters | string,
    type: BribesTypes,
): Promise<{
    count: number;
    bribes: (UpcomingBribe & Partial<MarketKey> & Partial<MarketVotes>)[];
}> => {
    try {
        // Build query parameters
        const params: UpcomingRequestParams = {
            limit: pageSize,
            page,
            ordering: sort,
            aqua_total_reward_amount_equivalent__gte: minBribeAmount,
        };

        if (weekFilter !== BribesWeeksFilters.all) {
            params.start_at__gte = weekFilter;
            params.start_at__lt = getNextWeekStartFromString(weekFilter);
        }

        if (type !== BribesTypes.all) {
            params.is_amm_protocol = type === BribesTypes.protocol;
        }

        // Fetch pending bribes
        const { data } = await axios.get<ListResponse<UpcomingBribe>>(
            `${getBribesApiUrl()}pending-bribes/`,
            { params },
        );

        // Merge bribe data with market information
        return await processBribes(data.results, data.count);
    } catch (error) {
        console.error('Failed to fetch upcoming bribes:', error);
        return { count: 0, bribes: [] };
    }
};
